import { Node, mergeAttributes } from '@tiptap/core';

import { AGREEMENT_VARIABLE_LABELS } from './agreement-variables';

// a "Choose One" block: its options are editable rich-text lines (bold,
// italic and variables work inside them). Staff resolve one option per group
// before the agreement is sent; selections are stored by option position, and
// the PDF prints one checkbox line per option with the selected one marked.
export const ChoiceNode = Node.create({
  name: 'choice',
  group: 'block',
  content: 'choiceOption+',
  defining: true,

  addAttributes() {
    return {
      id: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-choice-id') || '',
        renderHTML: (attributes) => ({ 'data-choice-id': attributes.id }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-choice-id]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { class: 'agreement-choice' }), 0];
  },
});

// one selectable option inside a choice; pressing Enter inside an option
// starts a new one
export const ChoiceOptionNode = Node.create({
  name: 'choiceOption',
  content: 'inline*',

  parseHTML() {
    return [{ tag: 'div[data-choice-option]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { 'data-choice-option': '', class: 'agreement-choice-option' }),
      0,
    ];
  },
});

export const newChoiceId = () =>
  `choice-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

export type ChoiceGroup = { id: string; options: string[] };

// the plain text of a template node, with variables shown as their labels
function nodeText(node: any): string {
  if (!node) {
    return '';
  }
  if (node.type === 'text') {
    return node.text || '';
  }
  if (node.type === 'variable') {
    return AGREEMENT_VARIABLE_LABELS[node.attrs?.key] || '';
  }
  if (node.type === 'customVariable') {
    return node.attrs?.name || '';
  }
  if (node.type === 'hardBreak') {
    return ' ';
  }
  return (node.content || []).map(nodeText).join('');
}

// mirrors CollectChoiceGroups in backend/app/utils/rentals_pdf — lists every
// choice authored into a template so the send dialog can require a selection
export function collectChoiceGroups(template: string): ChoiceGroup[] {
  if (!template) {
    return [];
  }
  let document: any;
  try {
    document = JSON.parse(template);
  } catch {
    return [];
  }
  const groups: ChoiceGroup[] = [];
  const walk = (node: any) => {
    if (!node) {
      return;
    }
    if (node.type === 'choice') {
      const optionNodes = (node.content || []).filter((child: any) => child.type === 'choiceOption');
      const options = optionNodes.length
        ? optionNodes.map(nodeText)
        : // templates saved before options became editable kept them in an attribute
          node.attrs?.options || [];
      groups.push({ id: node.attrs?.id || '', options });
    }
    (node.content || []).forEach(walk);
  };
  walk(document);
  return groups;
}
